import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Autocomplete,
  TextField
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import useFUBVerification from '../hooks/useFUBVerification'
import useAgents from '../hooks/useAgents'
import useDotloopAuth from '../hooks/useDotloopAuth'
import useDotloopProfiles from '../hooks/useDotloopProfiles'
import useDotloopTemplates from '../hooks/useDotloopTemplates'
import LoopCreationForm from '../components/LoopCreationForm'
import ApiDebugPanel from '../components/ApiDebugPanel'
import AdminStatusMenu from '../components/AdminStatusMenu'

export default function CreateLoopPage() {
  const navigate = useNavigate()
  const fubContext = useFUBVerification()
  const id = fubContext?.person?.id

  // DotLoop Authentication (must come before useAgents to provide dotloopEmail)
  const {
    authStatus,
    loading: authLoading,
    initiateAuth,
    logout,
    isAuthenticated,
    needsReauth,
    validationError,
    dotloopEmail
  } = useDotloopAuth(fubContext?.user?.id)

  // FUB Agents - defaults to authenticated Dotloop user, falls back to FUB assigned user
  const {
    agents,
    selectedAgent,
    loading: agentsLoading,
    error: agentsError,
    handleAgentChange
  } = useAgents(dotloopEmail, id)

  // DotLoop Profiles - only fetch when authenticated, re-fetch when email changes
  const {
    profiles,
    selectedProfile,
    loading: profilesLoading,
    error: profilesError,
    handleProfileChange,
    refreshProfiles
  } = useDotloopProfiles(isAuthenticated, dotloopEmail)

  // DotLoop Templates - fetch when a profile is selected
  const {
    templates,
    selectedTemplate,
    loading: templatesLoading,
    error: templatesError,
    handleTemplateChange
  } = useDotloopTemplates(selectedProfile?.id || null)
  
  // Force refresh profiles when email changes (handles account switch scenario)
  const previousEmailRef = useRef<string | null>(null);
  useEffect(() => {
    if (isAuthenticated && dotloopEmail && previousEmailRef.current !== null && previousEmailRef.current !== dotloopEmail) {
      // Email changed - force refresh profiles
      refreshProfiles();
    }
    previousEmailRef.current = dotloopEmail;
  }, [isAuthenticated, dotloopEmail, refreshProfiles]);

  // Debug state for API panel
  const [lastApiRequest, setLastApiRequest] = useState<any>(null)
  const [lastApiResponse, setLastApiResponse] = useState<any>(null)
  const [lastApiError, setLastApiError] = useState<string | null>(null)

  // Check if FUB API key is configured
  const hasFubApiKey = !!process.env.REACT_APP_FUB_API_KEY

  const handleBack = () => {
    // Preserve query params when navigating back
    const currentSearch = window.location.search
    navigate(`/${currentSearch}`)
  }

  const handleSuccess = () => {
    // Navigate back to home page after successful loop creation
    // Confetti and navigation handled in LoopCreationForm
  }

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      {/* Admin Status Menu - Top Right */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1000
        }}
      >
        <AdminStatusMenu
          isAuthenticated={isAuthenticated}
          authLoading={authLoading}
          onAuthenticate={initiateAuth}
          onLogout={logout}
          hasFubApiKey={hasFubApiKey}
          dotloopEmail={dotloopEmail}
          validationError={validationError}
          needsReauth={needsReauth}
        />
      </Box>

      {/* Debug Panel - Only in dev mode */}
      <ApiDebugPanel
        fubContext={fubContext}
        selectedAgent={selectedAgent}
        selectedProfile={selectedProfile}
        lastApiRequest={lastApiRequest}
        lastApiResponse={lastApiResponse}
        lastApiError={lastApiError}
      />

      {/* Back Button */}
      <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1000 }}>
        <IconButton onClick={handleBack} size="small">
          <ArrowBackIcon />
        </IconButton>
      </Box>

      {/* Main Content */}
      {authLoading ? (
        // Loading state
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            gap: 2
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Checking DotLoop connection...
          </Typography>
        </Box>
      ) : !isAuthenticated ? (
        // Not authenticated - Show centered connect button
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            px: 3,
            gap: 2
          }}
        >
          {/* Warning message if needs reauth */}
          {needsReauth && (
            <Alert 
              severity="warning" 
              sx={{ 
                maxWidth: 450,
                width: '100%'
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Authentication Required
              </Typography>
              <Typography variant="body2">
                Please connect your DotLoop account to create and manage transaction loops.
              </Typography>
            </Alert>
          )}
          
          <Card
            sx={{
              maxWidth: 400,
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              borderRadius: 3
            }}
          >
            <CardContent sx={{ py: 4 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Connect to DotLoop
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your DotLoop account to create and manage transactions
              </Typography>
              <Button
                variant="contained"
                size="large"
                onClick={initiateAuth}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                Connect to DotLoop
              </Button>
            </CardContent>
          </Card>
        </Box>
      ) : (
        // Authenticated - Show full form UI
        <Stack spacing={1.5} sx={{ pt: 5 }}>
          {/* Main Form Section */}
          <Stack spacing={1.5} sx={{ px: 1.5 }}>
            {/* Agent Selection */}
            <Autocomplete
              options={agents}
              value={selectedAgent}
              onChange={(_, newValue) => {
                if (newValue) {
                  handleAgentChange(newValue.id)
                }
              }}
              getOptionLabel={(agent) => {
                const name = `${agent.firstName} ${agent.lastName}`
                return agent.teamLeaderOf && agent.teamLeaderOf.length > 0
                  ? `${name} (Team Leader)`
                  : name
              }}
              filterOptions={(options, { inputValue }) => {
                const search = inputValue.toLowerCase()
                return options.filter((agent) => {
                  const name = `${agent.firstName} ${agent.lastName}`.toLowerCase()
                  return name.includes(search)
                })
              }}
              loading={agentsLoading}
              disabled={agentsLoading}
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Agent"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {agentsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText="No agents found"
            />

            {/* Agent Error */}
            {agentsError && <Alert severity="error">{agentsError}</Alert>}

            {/* DotLoop Profile Selection */}
            <FormControl fullWidth>
              <InputLabel id="profile-select-label">DotLoop Profile</InputLabel>
              <Select
                labelId="profile-select-label"
                id="profile-select"
                value={selectedProfile?.id || ''}
                label="DotLoop Profile"
                onChange={(e) => handleProfileChange(e.target.value as number)}
                disabled={profilesLoading}
              >
                {profilesLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading profiles...
                  </MenuItem>
                ) : profiles.length > 0 ? (
                  profiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      {profile.name}
                      {profile.default && ' (Default)'}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No profiles available</MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Profile Error */}
            {profilesError && <Alert severity="error">{profilesError}</Alert>}

            {/* DotLoop Template Selection (Optional) */}
            <FormControl fullWidth>
              <InputLabel id="template-select-label">
                Loop Template (Optional)
              </InputLabel>
              <Select
                labelId="template-select-label"
                id="template-select"
                value={selectedTemplate?.id || ''}
                label="Loop Template (Optional)"
                onChange={(e) => handleTemplateChange(e.target.value ? Number(e.target.value) : null)}
                disabled={templatesLoading || !selectedProfile}
              >
                <MenuItem value="">
                  <em>No Template</em>
                </MenuItem>
                {templatesLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading templates...
                  </MenuItem>
                ) : templates.length > 0 ? (
                  templates.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    {selectedProfile ? 'No templates available' : 'Select a profile first'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {/* Template Error */}
            {templatesError && <Alert severity="warning">{templatesError}</Alert>}
          </Stack>

          {/* Loop Creation Form */}
          <LoopCreationForm
            fubContext={fubContext}
            selectedAgent={selectedAgent}
            selectedProfile={selectedProfile}
            selectedTemplate={selectedTemplate}
            onApiRequest={setLastApiRequest}
            onApiResponse={setLastApiResponse}
            onApiError={setLastApiError}
            onSuccess={handleSuccess}
          />
        </Stack>
      )}
    </Box>
  )
}
